// GeneradorTarjetas.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Button, Card, CardContent, Grid, TextField, Typography,
    Alert, Collapse, CircularProgress, Popover, IconButton,
    Stack, Container, Autocomplete, ListItemText
} from '@mui/material';
import {
    Palette as PaletteIcon, Save as SaveIcon,
    Download as DownloadIcon, Refresh as RefreshIcon,
    Check as CheckIcon, Business as BusinessIcon,
    Visibility as VisibilityIcon, Person as PersonIcon,
    QrCode as QrCodeIcon
} from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import { getUsuariosSinTarjeta, crearTarjeta } from '../../../services/apis';
import { SubHeader } from '../../../utils/componentes-utiles/SubHeader';
import logoEmpresa from '../../../media/logoempresa.png';
import { canvasToBMP } from '../../../utils/utils';
import { useNavigate } from 'react-router-dom';

export const GeneradorTarjetas = ({ usuario, onGuardar, empresaNombre = '' }) => {
    const [colorPrimario, setColorPrimario] = useState('var(--principal)');
    const [colorSecundario, setColorSecundario] = useState('var(--secundario)');
    const [nombreEmpresa, setNombreEmpresa] = useState(empresaNombre?.trim() ? empresaNombre : 'JC INFORMATICS');

    const [modoId, setModoId] = useState('AUTO'); // 'AUTO' | 'RFID'
    const [rfidValue, setRfidValue] = useState('');

    const [opciones, setOpciones] = useState([]);
    const [usuariosCargando, setUsuariosCargando] = useState(false);
    const [usuariosError, setUsuariosError] = useState(null);
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

    const [nombrePersona, setNombrePersona] = useState('');
    const [codigoQR, setCodigoQR] = useState('');
    const [tarjetaGuardada, setTarjetaGuardada] = useState(null);

    const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
    const [colorPickerTipo, setColorPickerTipo] = useState('primario');
    const [cargando, setCargando] = useState(false);
    const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });

    const tarjetaRef = useRef(null);
    const qrWrapperRef = useRef(null);
    const rfidInputRef = useRef(null);

    useEffect(() => {
        setNombreEmpresa(empresaNombre?.trim() ? empresaNombre : 'JC INFORMATICS');
    }, [empresaNombre]);
    const getTextColor = (bgColor) => {
        // Quitar el #
        const color = bgColor.replace('#', '');

        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);

        // Fórmula de luminancia
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b);

        return luminance > 186 ? '#333' : '#fff'; // claro → texto oscuro, oscuro → texto blanco
    };
    const getCSSColor = (variable) => {
        return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
    };
    const hexPrimario = colorPrimario.startsWith('#') ? colorPrimario : getCSSColor('--principal');
    const hexSecundario = colorSecundario.startsWith('#') ? colorSecundario : getCSSColor('--secundario');
    const navigate = useNavigate();
    // -----------------------
    // Cargar usuarios sin tarjeta (centralizado)
    // -----------------------
    const cargarUsuariosSinTarjeta = async () => {
        setUsuariosCargando(true);
        setUsuariosError(null);
        try {
            const resp = await getUsuariosSinTarjeta();
            const usuariosArray = resp?.data?.usuarios ?? [];
            if (!Array.isArray(usuariosArray)) throw new Error('Formato inesperado de usuarios');
            const filtrados = usuariosArray.filter(u => Number(u.rol) !== 930 && Number(u.rol) !== 931);
            const normalized = filtrados.map(u => ({
                ...u,
                value: Number(u.id),
                label: u.nombre_completo || `${u.nombre || ''} ${u.apellidos || ''}`.trim() || u.mail || `#${u.id}`
            }));
            setOpciones(normalized);
            if (resp.success === false) {
                navigate('/');
            }
        } catch (err) {
            console.error('Error getUsuariosSinTarjeta:', err);
            setUsuariosError('Error al cargar usuarios (ver consola)');
            setOpciones([]);

        } finally {
            setUsuariosCargando(false);


        }
    };

    // Montaje: cargar usuarios sin tarjeta
    useEffect(() => {
        let mounted = true;
        if (mounted) cargarUsuariosSinTarjeta();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (usuarioSeleccionado?.value) {
            setNombrePersona(usuarioSeleccionado.label || `${usuarioSeleccionado.nombre || ''} ${usuarioSeleccionado.apellidos || ''}`.trim());
            setCodigoQR('');
            setTarjetaGuardada(null);
        } else {
            setNombrePersona('');
            setCodigoQR('');
            setTarjetaGuardada(null);
        }
    }, [usuarioSeleccionado]);

    const abrirColorPicker = (event, tipo) => { setColorPickerAnchor(event.currentTarget); setColorPickerTipo(tipo); };
    const cerrarColorPicker = () => setColorPickerAnchor(null);
    const seleccionarColor = (c) => { if (colorPickerTipo === 'primario') setColorPrimario(c); else setColorSecundario(c); cerrarColorPicker(); };

    const guardarDiseño = async () => {
        if (modoId === 'RFID') {
            const value = rfidValue.trim();

            if (!value) {
                setMensaje({ tipo: 'error', texto: 'Escanea una tarjeta RFID' });
                return;
            }

            if (!/^\d+$/.test(value)) {
                setMensaje({ tipo: 'error', texto: 'El código RFID debe ser numérico' });
                return;
            }
        }
        if (!nombreEmpresa.trim()) {
            setMensaje({ tipo: 'error', texto: 'El nombre de la empresa es requerido' });
            return;
        }

        if (!usuarioSeleccionado) {
            setMensaje({ tipo: 'error', texto: 'Selecciona un trabajador para la tarjeta' });
            return;
        }

        if (modoId === 'RFID' && !rfidValue.trim()) {
            setMensaje({ tipo: 'error', texto: 'Escanea una tarjeta RFID antes de guardar' });
            return;
        }

        setCargando(true);
        try {
            const diseñoData = {
                id_usuario: usuarioSeleccionado.value,
                nombre_empresa: nombreEmpresa,
                nombre_persona: nombrePersona,
                color_primario: colorPrimario.startsWith('#')
                    ? colorPrimario
                    : getCSSColor('--principal'),
                color_secundario: colorSecundario.startsWith('#')
                    ? colorSecundario
                    : getCSSColor('--secundario'),
                modelo: 1,
                id_manual: modoId === 'RFID' ? rfidValue.trim() : null
            };

            const resp = await crearTarjeta(diseñoData);

            // ❌ ERROR
            if (!resp || !resp.success) {
                setMensaje({
                    tipo: 'error',
                    texto: resp?.message || 'Error al crear la tarjeta'
                });
                return;
            }

            // ✅ GUARDADO CORRECTO
            const tarjeta = resp.tarjeta;
            setTarjetaGuardada(tarjeta);

            // QR solo para modo AUTO
            if (modoId === 'AUTO') {
                setCodigoQR(String(tarjeta.id));
            }
            else {
                setCodigoQR('');
            }

            // 🧹 RESET DE CAMPOS TRAS GUARDAR
            setRfidValue('');

            if (modoId === 'RFID') {
                // RFID → reset completo
                setUsuarioSeleccionado(null);
                setNombrePersona('');
                setCodigoQR('');
                setModoId('AUTO');
            }

            // Recargar lista de usuarios sin tarjeta
            await cargarUsuariosSinTarjeta();

            setMensaje({
                tipo: 'success',
                texto: 'Tarjeta guardada correctamente'
            });

            if (onGuardar) onGuardar(tarjeta);

        } catch (err) {
            console.error('guardarDiseño error:', err);
            setMensaje({
                tipo: 'error',
                texto: 'Error al guardar la tarjeta'
            });
        } finally {
            setCargando(false);
            setTimeout(() => setMensaje({ tipo: '', texto: '' }), 3000);
        }
    };

    const generarImagenDesdeQR = async (qrValueParam = null, tarjetaParam = null) => {
        const qrValue = qrValueParam ?? codigoQR;
        if (!qrValue) {
            setMensaje({ tipo: 'error', texto: 'No hay código QR disponible para generar la imagen' });
            return;
        }
        if (!nombreEmpresa.trim()) {
            setMensaje({ tipo: 'error', texto: 'Nombre de la empresa requerido' });
            return;
        }

        setCargando(true);
        try {
            const wrapper = qrWrapperRef.current;
            if (!wrapper) throw new Error('Referencia al QR no encontrada');
            const qrCanvas = wrapper.querySelector('canvas');
            if (!qrCanvas) throw new Error('Canvas del QR no encontrado (asegúrate que QRCodeCanvas se renderice)');

            const canvas = document.createElement('canvas');
            canvas.width = 1004;
            canvas.height = 649;
            const ctx = canvas.getContext('2d');

            // ------- Fondo gradiente -------
            const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, hexPrimario);
            gradient.addColorStop(1, hexSecundario);

            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // ------- Parámetros de layout -------
            const marginLeft = 60;
            const marginRight = 60;
            const headerTop = 20;
            const headerHeight = 80;
            const logoMaxH = 60;

            // tamaño de la caja QR
            const qrBoxWidth = 400;
            const qrBoxHeight = 400;
            const qrBoxX = canvas.width - marginRight - qrBoxWidth;

            // ancho disponible para texto (evitamos solapamiento con el QR)
            const gapBetweenTextAndQR = 24;
            const availableTextWidth = qrBoxX - marginLeft - gapBetweenTextAndQR;

            // ------- Cargar logo y dibujar en la derecha del header -------
            if (logoEmpresa) {
                await new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        const aspect = img.width / img.height || 1;
                        const h = Math.min(logoMaxH, img.height);
                        const w = h * aspect;
                        const logoX = canvas.width - marginRight - w; // logo a la derecha
                        const logoY = headerTop; // al nivel superior del header
                        try { ctx.drawImage(img, logoX, logoY, w, h); } catch (e) { /* ignore */ }
                        resolve();
                    };
                    img.onerror = () => resolve();
                    img.src = logoEmpresa;
                });
            }

            // ------- Empresa (header) -------
            ctx.save();
            ctx.fillStyle = 'white';
            ctx.font = 'bold 26px sans-serif';
            ctx.shadowColor = 'rgba(0,0,0,0.35)';
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            ctx.shadowBlur = 2;
            const empresaTextX = marginLeft;
            const empresaTextY = headerTop + 36;
            ctx.textAlign = 'left';
            ctx.fillText(nombreEmpresa, empresaTextX, empresaTextY);
            ctx.restore();

            // ------- Nombre persona: ajuste de tamaño y wrapping -------
            // función para partir en líneas con máximo ancho
            const wrapText = (ctx, text, maxWidth) => {
                const words = String(text).split(/\s+/);
                const lines = [];
                let line = '';
                for (let i = 0; i < words.length; i++) {
                    const test = line ? (line + ' ' + words[i]) : words[i];
                    const metrics = ctx.measureText(test);
                    if (metrics.width <= maxWidth) {
                        line = test;
                    } else {
                        if (line) {
                            lines.push(line);
                            line = words[i];
                        } else {
                            // una palabra muy larga: cortamos el word con slices
                            let w = words[i];
                            let chunk = '';
                            for (let c = 0; c < w.length; c++) {
                                const t = chunk + w[c];
                                if (ctx.measureText(t).width > maxWidth) {
                                    if (chunk) lines.push(chunk);
                                    chunk = w[c];
                                } else {
                                    chunk = t;
                                }
                            }
                            if (chunk) line = chunk;
                        }
                    }
                }
                if (line) lines.push(line);
                return lines;
            };

            // Elegir tamaño de fuente inicial y reducir si no encaja
            let fontSize = 64;
            const minFontSize = 28;
            ctx.textAlign = 'left';
            ctx.shadowColor = 'rgba(0,0,0,0.35)';
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            ctx.shadowBlur = 2;

            let lines = [];
            while (fontSize >= minFontSize) {
                ctx.font = `bold ${fontSize}px sans-serif`;
                // probamos a partir en líneas con el font actual
                lines = wrapText(ctx, nombrePersona || '', availableTextWidth);
                // si son 2-3 líneas razonables o la medida total cabe, aceptamos
                const totalHeight = lines.length * (fontSize * 1.05);
                // dejamos máximo (qrBoxHeight - 40) para nombre por si quieres forzar no solapar QR verticalmente
                if (lines.length <= 3 || fontSize === minFontSize) break;
                // si demasiadas líneas reducimos fontSize
                fontSize = Math.max(minFontSize, Math.floor(fontSize * 0.88));
            }

            // dibujar líneas del nombre (alineadas a la izquierda)
            ctx.save();
            ctx.fillStyle = 'white';
            ctx.font = `bold ${fontSize}px sans-serif`;
            ctx.textAlign = 'left';
            ctx.shadowColor = 'rgba(0,0,0,0.35)';
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            ctx.shadowBlur = 2;

            const personaX = marginLeft;
            // punto Y donde empezará la primera línea (debajo del header)
            const personaStartY = headerTop + headerHeight + 40;
            const lineHeight = fontSize * 1.05;
            for (let i = 0; i < lines.length; i++) {
                const y = personaStartY + (i * lineHeight);
                ctx.fillText(lines[i], personaX, y);
            }
            ctx.restore();

            // ------- Calculamos qrBoxY según el espacio ocupado por el nombre -------
            const lastLineY = personaStartY + (Math.max(1, lines.length - 1) * lineHeight);
            // añadimos un pequeño padding antes de la caja QR
            const qrGapTop = 24;
            const qrBoxY = lastLineY + (fontSize * 0.6) + qrGapTop;

            // Asegurarnos de que el QR no se salga del canvas verticalmente
            const maxQrY = canvas.height - qrBoxHeight - 60;
            const finalQrBoxY = Math.min(qrBoxY, maxQrY);

            // ------- Dibujar caja blanca del QR (con sombra y bordes redondeados) -------
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.25)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 4;
            const r = 10;
            ctx.fillStyle = 'white';
            ctx.beginPath();
            ctx.moveTo(qrBoxX + r, finalQrBoxY);
            ctx.lineTo(qrBoxX + qrBoxWidth - r, finalQrBoxY);
            ctx.quadraticCurveTo(qrBoxX + qrBoxWidth, finalQrBoxY, qrBoxX + qrBoxWidth, finalQrBoxY + r);
            ctx.lineTo(qrBoxX + qrBoxWidth, finalQrBoxY + qrBoxHeight - r);
            ctx.quadraticCurveTo(qrBoxX + qrBoxWidth, finalQrBoxY + qrBoxHeight, qrBoxX + qrBoxWidth - r, finalQrBoxY + qrBoxHeight);
            ctx.lineTo(qrBoxX + r, finalQrBoxY + qrBoxHeight);
            ctx.quadraticCurveTo(qrBoxX, finalQrBoxY + qrBoxHeight, qrBoxX, finalQrBoxY + qrBoxHeight - r);
            ctx.lineTo(qrBoxX, finalQrBoxY + r);
            ctx.quadraticCurveTo(qrBoxX, finalQrBoxY, qrBoxX + r, finalQrBoxY);
            ctx.closePath();
            ctx.fill();
            ctx.restore();

            // ------- Dibujar el canvas del QR dentro de la caja (centrado) -------
            const innerPadding = 8;
            const qrInnerSize = qrBoxWidth - innerPadding * 2;
            const qrInnerX = qrBoxX + innerPadding;
            const qrInnerY = finalQrBoxY + innerPadding;
            ctx.drawImage(qrCanvas, qrInnerX, qrInnerY, qrInnerSize, qrInnerSize);

            // ------- Footer con código centrado -------
            ctx.save();
            ctx.fillStyle = 'rgba(255,255,255,0.95)';
            ctx.font = '16px monospace';
            ctx.textAlign = 'center';
            ctx.fillText(`Código: ${qrValue}`, canvas.width / 2, canvas.height - 36);
            ctx.restore();

            // ------- Exportar BMP válido para Zebra ZC100 -------
            try {
                // Generar BMP real de 24 bits
                const bmpBlob = canvasToBMP(canvas);

                const safeEmpresa = (nombreEmpresa || 'empresa').replace(/\s+/g, '-');
                const filename = `tarjeta-${safeEmpresa}-${tarjetaParam?.id ?? 'n'}.bmp`;

                const link = document.createElement('a');
                link.href = URL.createObjectURL(bmpBlob);
                link.download = filename;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setTimeout(() => URL.revokeObjectURL(link.href), 200);

                setMensaje({ tipo: 'success', texto: 'Tarjeta generada en BMP correctamente' });

            } catch (err) {
                console.error('Error al generar BMP:', err);
                setMensaje({ tipo: 'error', texto: 'No se pudo generar la imagen BMP' });
            }


            setMensaje({ tipo: 'success', texto: 'Tarjeta generada y descargada' });
        } catch (err) {
            console.error('generarImagenDesdeQR error:', err);
            setMensaje({ tipo: 'error', texto: 'Error al generar la imagen de la tarjeta' });
        } finally {
            setCargando(false);
            setTimeout(() => setMensaje({ tipo: '', texto: '' }), 3000);
        }
    };

    const resetearDiseño = () => {
        setColorPrimario('#var(--principal)'); setColorSecundario('var(--secundario)');
        setUsuarioSeleccionado(null); setNombrePersona(''); setCodigoQR(''); setTarjetaGuardada(null);
        setMensaje({ tipo: '', texto: '' });
    };

    const coloresPredefinidos = ['#1976d2', '#2e7d32', '#ed6c02', '#var(--principal)', '#9c27b0', '#0288d1', '#388e3c', '#f57c00', '#c2185b', '#7b1fa2'];
    const logoUrl = logoEmpresa;

    return (
        <Container maxWidth="lg" sx={{ py: 2 }}>
            <SubHeader titulo={'Tarjetas'} />

            <Collapse in={!!mensaje.texto}>
                <Alert severity={mensaje.tipo} sx={{ mb: 2 }} onClose={() => setMensaje({ tipo: '', texto: '' })}>
                    {mensaje.texto}
                </Alert>
            </Collapse>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h5">Generador de Tarjetas de Fichaje</Typography>
                <Typography variant="body2" color="text.secondary">Crea tarjetas con código QR para fichar</Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Card><CardContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2"><BusinessIcon sx={{ mr: 1 }} /> Crea la tarjeta corporativa</Typography>
                            </Box>



                            <Box>
                                <Typography variant="subtitle2">Tipo de tarjeta</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            setModoId('AUTO');
                                            setRfidValue('');
                                        }}
                                        sx={{
                                            backgroundColor: colorPrimario,
                                            color: '#fff',
                                            opacity: modoId === 'AUTO' ? 1 : 0.5,
                                            border: modoId === 'AUTO' ? 'none' : '2px solid rgba(255,255,255,0.4)',
                                            boxShadow: modoId === 'AUTO' ? undefined : 'none',
                                            '&:hover': {
                                                backgroundColor: colorPrimario,
                                                opacity: 0.85
                                            }
                                        }}
                                    >
                                        QR (automático)
                                    </Button>

                                    <Button
                                        variant="contained"
                                        onClick={() => setModoId('RFID')}
                                        sx={{
                                            backgroundColor: colorSecundario,
                                            color: getTextColor(colorSecundario),
                                            opacity: modoId === 'RFID' ? 1 : 0.5,
                                            border: modoId === 'RFID' ? 'none' : '2px solid rgba(0,0,0,0.25)',
                                            boxShadow: modoId === 'RFID' ? undefined : 'none',
                                            '&:hover': {
                                                backgroundColor: colorSecundario,
                                                opacity: 0.85
                                            }
                                        }}
                                    >
                                        RFID
                                    </Button>
                                </Stack>
                            </Box>

                            {modoId === 'RFID' && (
                                <Box>
                                    <Typography variant="caption">
                                        Escanea la tarjeta RFID
                                    </Typography>

                                    <TextField
                                        inputRef={rfidInputRef}
                                        label="Código RFID"
                                        value={rfidValue}
                                        onChange={(e) => setRfidValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                guardarDiseño();
                                            }
                                        }}
                                        fullWidth
                                        autoComplete="off"
                                        inputProps={{
                                            inputMode: 'numeric'
                                        }}
                                    />

                                    <Typography variant="caption" color="text.secondary">
                                        El lector RFID rellenará este campo automáticamente
                                    </Typography>
                                </Box>
                            )}



                            <Box>
                                <Typography variant="subtitle2"><PaletteIcon sx={{ mr: 1 }} /> Colores de la Tarjeta</Typography>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            onClick={(e) => abrirColorPicker(e, 'primario')}
                                            fullWidth
                                            sx={{
                                                backgroundColor: colorPrimario,
                                                color: getTextColor(colorPrimario),
                                            }}
                                        >
                                            Primario
                                        </Button>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            onClick={(e) => abrirColorPicker(e, 'secundario')}
                                            fullWidth
                                            sx={{
                                                backgroundColor: colorSecundario,
                                                color: getTextColor(colorSecundario),
                                            }}
                                        >
                                            Secundario
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2"><PersonIcon sx={{ mr: 1 }} /> Información</Typography>
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Empresa</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{nombreEmpresa || 'JC INFORMATICS'}</Typography>
                                    </Box>

                                    <Autocomplete
                                        options={opciones}
                                        getOptionLabel={(o) => o?.label ?? ''}
                                        loading={usuariosCargando}
                                        value={usuarioSeleccionado}
                                        onChange={(e, newVal) => setUsuarioSeleccionado(newVal ?? null)}
                                        isOptionEqualToValue={(option, value) => (option?.value ?? option?.id) === (value?.value ?? value?.id)}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.value}>
                                                <ListItemText primary={option.label} secondary={option.mail ?? `ID: ${option.value}`} />
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Selecciona Trabajador *"
                                                size="small"
                                                helperText={usuariosError ? usuariosError : ''}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                                                    endAdornment: (
                                                        <>
                                                            {usuariosCargando ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    )
                                                }}
                                            />
                                        )}
                                        noOptionsText={usuariosCargando ? 'Cargando usuarios...' : 'No hay usuarios o tienen tarjeta'}
                                        clearOnEscape
                                        filterSelectedOptions
                                        PopperProps={{ style: { zIndex: 1400 } }}
                                    />

                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" display="block" gutterBottom><QrCodeIcon sx={{ mr: 0.5, fontSize: 16 }} /> Código QR para fichar</Typography>
                                        <Typography variant="body2" sx={{ backgroundColor: 'grey.100', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                            {codigoQR || 'Guarda la tarjeta para generar el código QR (ID)'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Este código único se usará para registrar fichajes</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    startIcon={cargando ? <CircularProgress size={20} /> : <SaveIcon />}
                                    onClick={guardarDiseño}
                                    disabled={cargando || !usuarioSeleccionado}
                                    fullWidth
                                    sx={{ mb: 1 }}
                                >
                                    {cargando ? 'Guardando...' : 'Guardar Tarjeta'}
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => generarImagenDesdeQR(null, tarjetaGuardada)}
                                    disabled={cargando || !tarjetaGuardada || modoId === 'RFID'}
                                    fullWidth
                                    sx={{ mb: 1 }}
                                >
                                    Descargar Tarjeta
                                </Button>

                                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetearDiseño} disabled={cargando} color="primary" fullWidth>
                                    Restablecer
                                </Button>
                            </Box>
                        </Stack>
                    </CardContent></Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card><CardContent>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6"><VisibilityIcon sx={{ mr: 1 }} /> Vista Previa de la Tarjeta</Typography>
                        </Box>

                        <Box ref={tarjetaRef} sx={{
                            width: '100%',
                            maxWidth: 600,
                            height: 350,
                            background: `linear-gradient(135deg, ${hexPrimario} 0%, ${hexSecundario} 100%)`,
                            borderRadius: 2,
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            mx: 'auto',
                            boxShadow: 3
                        }}>
                            {/* Header: nombre empresa (izquierda) y logo (derecha) */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2
                            }}>
                                <Box>
                                    <Typography sx={{
                                        fontSize: '1.8rem',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                    }}>
                                        {nombreEmpresa || 'JC INFORMATICS'}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}>
                                    <Box component="img"
                                        src={logoUrl}
                                        alt="Logo de la empresa"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                        sx={{
                                            height: 60,
                                            width: 'auto',
                                            maxWidth: 180,
                                            objectFit: 'contain',
                                            filter: 'brightness(0) invert(1)'
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Contenido principal: nombre persona a la izquierda, QR a la derecha (más grande y más abajo) */}
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 3
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    {nombrePersona && (
                                        <Typography sx={{
                                            fontSize: '2.2rem',
                                            fontWeight: 'bold',
                                            color: 'white',
                                            mb: 1,
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                                        }}>
                                            {nombrePersona}
                                        </Typography>
                                    )}
                                    <Typography sx={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontSize: '1rem'
                                    }}>
                                        Tarjeta de identificación
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 160,
                                    height: 160,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    mt: 3
                                }}>
                                    {codigoQR && (
                                        <QRCodeCanvas value={codigoQR} size={120} includeMargin={false} level="L" />
                                    )}

                                    {!codigoQR && modoId === 'RFID' && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.9rem',
                                                color: '#333',
                                                fontWeight: 'bold',
                                                textAlign: 'center'
                                            }}
                                        >
                                            TARJETA RFID
                                        </Typography>
                                    )}
                                    <Typography variant="caption" sx={{
                                        textAlign: 'center',
                                        color: '#333',
                                        fontWeight: 'bold',
                                        fontSize: '0.7rem',
                                        mt: 1
                                    }}>
                                        ESCANEAR PARA FICHAR
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                <Typography sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                    Código: {codigoQR ? (codigoQR.length > 20 ? `${codigoQR.substring(0, 20)}...` : codigoQR) : 'XXXX-XXXX-XXXX'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Esta tarjeta incluye un código QR único para registrar fichajes</Typography>
                        </Box>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Popover open={Boolean(colorPickerAnchor)} anchorEl={colorPickerAnchor} onClose={cerrarColorPicker} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <Box sx={{ p: 2, width: 250 }}>
                    <Typography variant="subtitle2" gutterBottom>Color {colorPickerTipo === 'primario' ? 'Primario' : 'Secundario'}</Typography>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        {coloresPredefinidos.map((c) => (
                            <Grid item xs={4} key={c}>
                                <IconButton onClick={() => seleccionarColor(c)} sx={{ backgroundColor: c, width: 40, height: 40, border: '2px solid white', '&:hover': { opacity: 0.8 } }}>
                                    {(colorPickerTipo === 'primario' && c === colorPrimario) || (colorPickerTipo === 'secundario' && c === colorSecundario) ? <CheckIcon sx={{ fontSize: 16, color: 'white' }} /> : null}
                                </IconButton>
                            </Grid>
                        ))}
                    </Grid>

                    <Box>
                        <Typography variant="caption" gutterBottom>Color personalizado:</Typography>
                        <input type="color" value={colorPickerTipo === 'primario' ? colorPrimario : colorSecundario} onChange={(e) => { if (colorPickerTipo === 'primario') setColorPrimario(e.target.value); else setColorSecundario(e.target.value); }} style={{ width: '100%', height: 40, borderRadius: 4, border: 'none', cursor: 'pointer' }} />
                    </Box>

                    <Button fullWidth onClick={cerrarColorPicker} sx={{ mt: 2 }} variant="contained">Aplicar Color</Button>
                </Box>
            </Popover>

            {codigoQR && (
                <div
                    style={{
                        position: 'absolute',
                        left: -9999,
                        top: -9999,
                        width: 1,
                        height: 1,
                        overflow: 'hidden'
                    }}
                    ref={qrWrapperRef}
                >
                    <QRCodeCanvas value={codigoQR} size={256} includeMargin={false} level="L" />
                </div>
            )}
        </Container>
    );
};
